import {format} from 'date-fns';
import {TFunction} from 'i18next';
import {call, put, all, takeLatest} from 'redux-saga/effects';
import {actions, ActionType} from './action';
import {getResultUseTicket} from './apiGet';
import {
  API_AUTH,
  getAccessToken,
  saveReturnPage,
  sendAuthEvent,
  sendAuthViewImp,
} from '../../auth/common';
import BirthdayModalDesc, {
  BIRTHDAY_P_ALERT_GROUP_ID,
  getFormattedDate,
} from '../../auth/component/BirthdayModalDesc';
import {actions as authActions} from '../../auth/state/action';
import {getUser} from '../../auth/state/apiGet';
import {pAlert} from '../../common/component/PAlert';
import {AUTH_HOST, REGION} from '../../common/constant';
import {Page} from '../../common/constant/page';
import {pToast} from '../../common/container/PToast';
import {showAuthModal, showIdVerificationModal} from '../../common/function';
import {selectT} from '../../common/i18n';
import {ITicketType} from '../../common/type';
import {ButtonSet} from '../../common/type/alert';
import callApi, {I_ApiResult} from '../../common/util/api';
import {
  parseDate,
  getIsToday,
  DEFAULT_DATE_FORMAT,
} from '../../common/util/date';
import {makeFetchSaga} from '../../common/util/fetch';
import {routeBack, routeTo} from '../../common/util/PageRouter';
import {typedSelect} from '../../common/util/saga';
import {
  getLocalStorageObject,
  setLocalStorageObject,
} from '../../common/util/storage';
import {toSnakeCase} from '../../common/util/string';
import {actions as contentActions} from '../../content/state/action';
import {callApiUser, getIsProhibitedEpisode} from '../function';
import {contentAdultMap} from '../hook/useRouteToContentObserver';
import {IMyTicket, IUseTicketParams} from '../type';

function callApiVerifyUserBirthday(birthday: string) {
  return call(callApi, {
    apiHost: AUTH_HOST,
    url: `${API_AUTH}/auth/id/verify/birthday`,
    method: 'post',
    headers: {
      'content-type': 'application/x-www-form-urlencoded',
    },
    params: {
      access_token: getAccessToken(),
      birth_day: birthday?.replace(/-/g, ''),
    },
  });
}

function* showVerifyBirthdayPopup() {
  sendAuthViewImp({
    customProps: {
      event_layer: 'popup',
      popup_type: 'content_identification',
    },
    viewImpContents: {name: 'popup'},
  });
  const t = yield* selectT();
  const {okClicked, isDimmedClicked} = yield call(pAlert, {
    groupId: BIRTHDAY_P_ALERT_GROUP_ID,
    title: t('authentication_global_popup_title'),
    desc: <BirthdayModalDesc />,
    buttonSet: ButtonSet.CancelAndConfirm,
    isTextAlignCenter: false,
  });
  if (okClicked) {
    const birthday = yield* typedSelect(state => state.auth.birthday);
    if (!birthday) return false;
    sendAuthEvent({
      actionName: 'popup_ok',
      customProps: {
        event_layer: 'popup',
        popup_type: 'content_identification',
      },
    });

    if (REGION === 'tha' || REGION === 'twn') {
      const {closeClicked} = yield call(pAlert, {
        groupId: BIRTHDAY_P_ALERT_GROUP_ID,
        title: t('authentication_global_confirm_popup_content', {
          value: format(parseDate(birthday), DEFAULT_DATE_FORMAT),
        }),
        buttonSet: ButtonSet.CancelAndConfirm,
        isTextAlignCenter: true,
      });

      if (closeClicked) return false;
    }

    const {success, data} = yield callApiVerifyUserBirthday(birthday);
    if (success && data) {
      const {success, data} = yield call(callApiUser, true);
      if (!success || !data) return false;
      const user = getUser(data, getAccessToken());
      yield put(authActions.setValue('user', user));
      if (user.isAdult) {
        pToast(t =>
          t('authentication_global_succeed_adult_toast', {
            n: getFormattedDate(birthday),
          })
        );
        return true;
      } else {
        pToast(t => t('authentication_global_failed_adult_toast'));
        return false;
      }
    }
    return false;
  } else {
    sendAuthEvent({
      actionName: 'popup_cancel',
      customProps: {
        event_layer: isDimmedClicked ? 'dimmed' : 'popup',
        popup_type: 'content_identification',
      },
    });
    return false;
  }
}

const VISITED_DATE_ADULT_CONTENT_KEY = 'visited-adult-content';
function getIsVisitedAdultContent({
  isLogin,
  contentId,
}: {
  isLogin: boolean;
  contentId: number;
}) {
  const visitedDateAdultContents = getLocalStorageObject<{
    [key: number]: string;
  }>({key: VISITED_DATE_ADULT_CONTENT_KEY});
  const dateString = visitedDateAdultContents?.[contentId];
  if (!dateString) {
    return false;
  } else if (isLogin) {
    return true;
  } else {
    const visitedDate = parseDate(dateString).getTime();
    return getIsToday(visitedDate);
  }
}
function saveVisitedDateAdultContent(contentId: number) {
  const visitedDateAdultContents =
    getLocalStorageObject<{[key: number]: string}>({
      key: VISITED_DATE_ADULT_CONTENT_KEY,
    }) || {};
  visitedDateAdultContents[contentId] = format(Date.now(), 'yyyy-MM-dd');
  setLocalStorageObject({
    key: VISITED_DATE_ADULT_CONTENT_KEY,
    value: visitedDateAdultContents,
  });
}

function* showAdultConfirmPopup(contentId: number) {
  const {okClicked} = yield call(pAlert, {
    title: t => t('login_adult_verify_popup'),
    buttonSet: ButtonSet.CancelAndConfirm,
    labelConfirm: t => t('common_yes'),
    labelClose: t => t('common_no'),
  });
  if (okClicked) {
    saveVisitedDateAdultContent(contentId);
    return true;
  } else {
    return false;
  }
}

function* processVerifyAdult(params: IUseTicketParams) {
  const user = yield* typedSelect(state => state.auth.user);
  const isLogin = !!user;
  const {contentId, isFree} = params;
  if (REGION === 'idn') {
    if (!isFree && !isLogin) {
      showAuthModal({titleType: 'adult'});
      return false;
    } else if (getIsVisitedAdultContent({isLogin, contentId})) {
      return true;
    } else {
      return yield* showAdultConfirmPopup(params.contentId);
    }
  } else {
    if (!isLogin) {
      showAuthModal({titleType: 'adult'});
      return false;
    } else if (!user.isIdentityVerified) {
      return yield* showVerifyBirthdayPopup();
    } else if (user.isChild) {
      pToast(t => t('authentication_global_failed_adult_toast'));
      return false;
    } else {
      return true;
    }
  }
}

function* fetchTimeout() {
  yield call(callApi, {
    url: 'https://httpstat.us/500?sleep=7000',
  });
}

function* fetchUseTicket(action: ReturnType<typeof actions.fetchUseTicket>) {
  const params = action.payload;
  const {
    contentId,
    episodeId,
    isReadAgainAfterExpire,
    ticketType,
    authModalTitleType,
    entryType = 'default',
    isCheckNextEpisode = false,
    isCheckedAdult = false,
    isStopSalesEpisode,
  } = params;
  const isViewerDirect = entryType === 'viewerDirect';
  const isViewerEpList = entryType === 'viewerEpList';
  const isAdultContent = contentAdultMap[contentId];
  if (isAdultContent && REGION !== 'kor' && !isCheckedAdult) {
    const canContinue = yield* processVerifyAdult(params);
    yield put(authActions.setValue('birthday', undefined));
    if (!canContinue) {
      isViewerDirect && routeBack();
      return;
    }
  }

  const user = yield* typedSelect(state => state.auth.user);
  const isLogin = !!user;

  if (isStopSalesEpisode && !isLogin) {
    showAuthModal({titleType: 'loginButton'});
    return;
  }

  const url = `/episode/v3/episodes/${episodeId}/pass`;
  const {success, data}: I_ApiResult<any> = yield call(callApi, {
    url,
    method: 'post',
    data: {
      readAgain: isReadAgainAfterExpire,
      ticketType: ticketType && toSnakeCase(ticketType),
    },
    processApiErrorCallback: error => {
      if (isViewerDirect && error.errorType === 'UNDER_AGE_GRADE') {
        routeTo({page: Page.Home});
        return true;
      } else if (error.errorType === 'EMPTY_ACCESS_TOKEN') {
        showAuthModal({
          titleType: isAdultContent ? 'adult' : authModalTitleType,
          pageInfoAfterClose: isViewerDirect ? {page: Page.Home} : undefined,
        });
        return true;
      } else if (error.errorType === 'USER_VERIFICATION_REQUIRED') {
        saveReturnPage();
        showIdVerificationModal('adult', {page: Page.Home});
        return true;
      } else if (getIsProhibitedEpisode(error.errorType)) {
        pAlert({
          groupId: 'use-ticket-is-prohibited-episode',
          title: t => t('contenthome_ep_tab_stop_sell_popup'),
        });
        return true;
      }
      return false;
    },
  });
  if (success && data) {
    const t = yield* selectT();
    const resultUseTicket = getResultUseTicket({data, t});
    const {
      messageType,
      isPass,
      myTicket,
      waitfree,
      nextEpisode,
      targetEpisode,
      ticketType: responseTicketType,
      canNotUseWaitForFree,
    } = resultUseTicket;
    switch (messageType) {
      case 'ok':
        if (responseTicketType) {
          const isRunMode = yield* typedSelect(
            state => state.viewer.isRunMode && !isViewerEpList
          );
          if (isRunMode) {
            yield put(
              actions.fetchUseTicket({
                ...params,
                ticketType: responseTicketType,
                isCheckedAdult: true,
              })
            );
          } else {
            yield call(showConfirmUseTicket, {
              responseTicketType,
              canNotUseWaitForFree,
              params,
            });
          }
        }

        break;
      case 'invalidEpisode':
        yield call(pAlert, {
          groupId: 'use-ticket-invalid-episode',
          title: t('contenthome_stop_sell_popup'),
        });
        isViewerDirect && routeBack();
        break;
      case 'freeEpisode':
      case 'haveLicense':
        if (isPass) {
          yield call(routeToViewer, params);
        }
        break;
      case 'usePossession':
        if (isPass) {
          pToastUseTicket('contenthome_ticketused_possession_snackbar');
          yield call(routeToViewer, params);
        }
        break;
      case 'useRental':
        if (isPass) {
          pToastUseTicket('contenthome_ticketused_rental_snackbar');
          yield call(routeToViewer, params);
        }
        break;
      case 'useGift':
        if (isPass) {
          pToastUseTicket('contenthome_ticketused_gift_snackbar');
          yield call(routeToViewer, params);
        }
        break;
      case 'useWaitForFree':
        if (isPass) {
          pToastUseTicket('contenthome_ticketused_gidamu_snackbar');
          yield call(routeToViewer, params);
        }
        break;
      case 'noTicket':
        routeToTicketPurchase(params);
        break;
      case 'haveBothRentalAndPossession':
        yield call(showTicketSelectionPopup, {
          params,
          myTicket,
          responseTicketType,
        });
        break;
      case 'noWaitForFreeEpisode': {
        // 기다무 티켓은 사용할 수 없다.
        if (
          getMyTicketType({
            myTicket,
            canNotUseWaitForFree: true,
            ticketType: responseTicketType,
          }) === 'empty'
        ) {
          routeToTicketPurchase(params);
        } else {
          /**
           * 기다무 사용 불가 회차일 때 (최신 N편)
           * - 확인 클릭 => 대여/소장권 모두 있다면 => 대여/소장권 선택 팝업 노출
           * - 확인 클릭 => 대여 or 소장권 중 하나만 있을 때 => 바로 사용
           */
          yield call(showTicketUseFailedPopup, {
            messageType,
            params,
            myTicket,
            canNotUseWaitForFree,
            responseTicketType,
            t,
          });
        }
        break;
      }
      case 'alreadyRented':
      case 'alreadyRentedExcludedEpisode': {
        if (nextEpisode && isCheckNextEpisode) {
          const {firstClicked, closeClicked} = yield call(pAlert, {
            groupId: 'use-ticket-already-rented',
            title: t('contenthome_expired_popup'),
            buttonSet: ButtonSet.CancelAndTwoConfirm,
            labelConfirm: t('contenthome_expired_popup_viewed_button'),
            labelSecondConfirm: t('contenthome_expired_popup_next_button'),
            isTextAlignCenter: true,
            isButtonVertical: true,
          });
          if (!closeClicked) {
            yield call(processClickedViewAgain, {
              params: firstClicked
                ? params
                : {
                    ...params,
                    episodeId: nextEpisode.id,
                    episodeSid: nextEpisode.sid,
                  },
              myTicket,
              isReadAgainAfterExpire: true,
              responseTicketType,
              canNotUseWaitForFree,
            });
          }
        } else {
          yield call(showTicketUseFailedPopup, {
            messageType,
            excludedEpisodeCount: waitfree?.excludedEpisodeCount,
            params,
            myTicket,
            canNotUseWaitForFree,
            isReadAgainAfterExpire: true,
            responseTicketType,
            t,
          });
        }
        break;
      }
      default:
        break;
    }
    if (isPass && targetEpisode) {
      const {type, timeText, badge} = targetEpisode;
      yield put(
        contentActions.setEpisodeRead({
          contentId,
          episodeId,
          type,
          timeText,
          badge,
        })
      );
    }
  }
}

function pToastUseTicket(key: string) {
  pToast({
    desc: t => t(key, {value: 1}),
    variant: 'black',
  });
}

function* showTicketUseFailedPopup({
  messageType,
  params,
  myTicket,
  canNotUseWaitForFree,
  isReadAgainAfterExpire,
  responseTicketType,
  t,
}: {
  messageType:
    | 'noWaitForFreeEpisode'
    | 'alreadyRented'
    | 'alreadyRentedExcludedEpisode';
  excludedEpisodeCount?: number;
  params: IUseTicketParams;
  myTicket: IMyTicket;
  canNotUseWaitForFree: boolean;
  isReadAgainAfterExpire?: boolean;
  responseTicketType?: ITicketType;
  t: TFunction;
}) {
  const myTicketType = getMyTicketType({
    myTicket,
    canNotUseWaitForFree,
    ticketType: responseTicketType,
  });
  const isRunMode = yield* typedSelect(
    state => state.viewer.isRunMode && params.entryType !== 'viewerEpList'
  );
  const titleKey = EXCEPTION_TITLE_KEY[messageType][myTicketType];
  const isRented = messageType !== 'noWaitForFreeEpisode';
  if ((!isRented && myTicketType === 'rentalAndPossession') || isRunMode) {
    yield call(processClickedViewAgain, {
      params,
      myTicket,
      isReadAgainAfterExpire,
      responseTicketType,
      canNotUseWaitForFree,
    });
  } else {
    const {okClicked} = yield call(pAlert, {
      groupId: 'use-ticket-failed',
      title: t(titleKey ?? ''),
      desc: canNotUseWaitForFree
        ? t('contenthome_latest_ep_gidamu_popup')
        : undefined,
      buttonSet: ButtonSet.CancelAndConfirm,
    });
    if (okClicked) {
      yield call(processClickedViewAgain, {
        params,
        myTicket,
        isReadAgainAfterExpire,
        responseTicketType,
        canNotUseWaitForFree,
      });
    }
  }
}

function* processClickedViewAgain({
  params,
  myTicket,
  isReadAgainAfterExpire,
  responseTicketType,
  canNotUseWaitForFree,
}: {
  params: IUseTicketParams;
  myTicket: IMyTicket;
  isReadAgainAfterExpire?: boolean;
  responseTicketType?: ITicketType;
  canNotUseWaitForFree: boolean;
}) {
  const myTicketType = getMyTicketType({
    myTicket,
    canNotUseWaitForFree,
    ticketType: responseTicketType,
  });
  if (myTicketType === 'empty') {
    routeToTicketPurchase(params);
  } else if (myTicketType === 'rentalAndPossession') {
    yield call(showTicketSelectionPopup, {
      params,
      myTicket,
      isReadAgainAfterExpire,
      responseTicketType,
    });
  } else {
    yield put(
      actions.fetchUseTicket({
        ...params,
        isReadAgainAfterExpire,
        // 상황에 따라 responseTicketType이 없는 경우가 있다.
        // 그렇게 하면 팝업이 계속 뜨기 때문에, (없는 경우) myTicketType을 넣어준다.
        // https://kakaopagecorp.slack.com/archives/G01EWEP04J3/p1627525616361300
        ticketType: responseTicketType || myTicketType,
        isCheckedAdult: true,
      })
    );
  }
}

function* showTicketSelectionPopup({
  params,
  myTicket,
  isReadAgainAfterExpire,
  responseTicketType,
}: {
  params: IUseTicketParams;
  myTicket: IMyTicket;
  isReadAgainAfterExpire?: boolean;
  responseTicketType?: ITicketType;
}) {
  const t = yield* selectT();
  const {rentTicketCount, buyTicketCount} = myTicket;
  const {secondClicked, closeClicked} = yield call(pAlert, {
    groupId: 'use-ticket-ticket-selection',
    title: t('contenthome_ticket_option_popup'),
    desc: `${t('contenthome_popup_ticket_rental', {
      count: rentTicketCount,
    })} / ${t('contenthome_popup_ticket_possession', {
      count: buyTicketCount,
    })}`,
    buttonSet: ButtonSet.CancelAndTwoConfirm,
    labelConfirm: t('contenthome_popup_rent_button'),
    labelSecondConfirm: t('contenthome_popup_possess_button'),
    isTextAlignCenter: true,
    isButtonVertical: true,
  });
  if (!closeClicked) {
    /**
     * (신규고객혜택 대여권 | 선물 대여권 | 대여권)과 소장권을 같이 소유한다면 티켓 선택 팝업이 뜬다.
     * 소장권을 선택(secondClicked)했다면 소장권 소진,
     * 대여권을 선택(firstClicked했다면 (신규고객혜택 대여권 | 선물 대여권 | 대여권) 중 하나가 소진되는데
     * 명확한 ticketType을 명시하고 api를 재호출하지 않으면 소진 불가하다.
     * 그래서 responseTicketType이 gift | welcomeGift | waitForFree 일때는 responseTicket을 사용하고
     * 그렇지 않을때는 rental을 사용한다. (클라에서 정확히 알수 없음)
     *
     * 참고)
     * - 티켓 소진 순서 참고: 신규고객혜택 대여권 > 기다무 > 선물 대여권 > 소장권
     * - 소장권을 가졌더라도 기다무 티켓은 자동 소진한다
     */
    const ticketType = secondClicked
      ? 'possession'
      : responseTicketType || 'rental';
    yield put(
      actions.fetchUseTicket({
        ...params,
        ticketType,
        isReadAgainAfterExpire,
        isCheckedAdult: true,
      })
    );
  }
}

function* showConfirmUseTicket({
  responseTicketType,
  canNotUseWaitForFree,
  params,
}: {
  responseTicketType: ITicketType;
  canNotUseWaitForFree: boolean;
  params: IUseTicketParams;
}) {
  const t = yield* selectT();
  const {okClicked} = yield call(pAlert, {
    groupId: 'use-ticket-confirm',
    title: t(TICKET_USE_CONFIRM_MSG_MAP[responseTicketType]),
    desc: canNotUseWaitForFree
      ? t('contenthome_latest_ep_gidamu_popup')
      : undefined,
    buttonSet: ButtonSet.CancelAndConfirm,
  });
  if (okClicked) {
    yield put(
      actions.fetchUseTicket({
        ...params,
        ticketType: responseTicketType,
        isCheckedAdult: true,
      })
    );
  }
}

function routeToTicketPurchase({
  contentId,
  contentSid,
  episodeId,
  episodeSid,
}: IUseTicketParams) {
  routeTo({
    page: Page.ContentPurchase,
    id: contentId,
    sid: contentSid,
    episodeId,
    episodeSid,
    isTryReadAgain: true,
  });
}

function* routeToViewer({
  episodeId,
  episodeSid,
  entryType,
}: Pick<
  IUseTicketParams,
  'contentId' | 'episodeId' | 'episodeSid' | 'entryType'
>) {
  // TODO: 뷰어에서 헤더의 뒤로가기 버튼의 동작 방식에 따라 isShallow: false 대신 isReplace: false로 바뀔 수도 있다. (모아나 확인중)
  // https://podotree.atlassian.net/wiki/spaces/KW/pages/1391591565?focusedCommentId=1937375367#comment-1937375367
  if (entryType === 'content') {
    routeTo(
      {page: Page.Viewer, id: episodeId, sid: episodeSid},
      {isShallow: false}
    );
  } else if (entryType !== 'viewerDirect') {
    routeTo(
      {page: Page.Viewer, id: episodeId, sid: episodeSid},
      {isShallow: false}
    );
  }
}

function getMyTicketType({
  myTicket,
  canNotUseWaitForFree,
  ticketType,
}: {
  myTicket: IMyTicket;
  canNotUseWaitForFree: boolean;
  ticketType?: ITicketType;
}): IMyTicketType {
  // 대여권(기다무 포함)과 소장권 둘다 있는 경우에만 티켓 선택할 수 있도록 처리
  // 기다무 작품이라면 마지막 회차를 소장할 수 없는 문제가 있기 때문
  // 둘다 있는 경우가 아니라면 API에서 내려주는 ticketType 사용.
  const {rentTicketCount, buyTicketCount} = myTicket;
  const hasRentTicket =
    (rentTicketCount === 1 && !canNotUseWaitForFree) || rentTicketCount > 1;
  const defaultTicketType = hasRentTicket
    ? 'rental'
    : buyTicketCount
      ? 'possession'
      : 'empty';
  return (
    (hasRentTicket && buyTicketCount ? 'rentalAndPossession' : ticketType) ||
    defaultTicketType
  );
}

const TICKET_USE_CONFIRM_MSG_MAP: {[key in ITicketType]: string} = {
  rental: 'contenthome_ticketused_rental_popup',
  possession: 'contenthome_ticketused_possession_popup',
  gift: 'contenthome_ticketused_gift_popup',
  waitForFree: 'contenthome_ticketused_gidamu_popup',
  welcomeGift: 'contenthome_ticketused_gift_popup',
};

type IMyTicketType = ITicketType | 'empty' | 'rentalAndPossession';

const EXCEPTION_TITLE_KEY: {
  [key in
    | 'noWaitForFreeEpisode'
    | 'alreadyRented'
    | 'alreadyRentedExcludedEpisode']: {
    [key in IMyTicketType]?: string;
  };
} = {
  noWaitForFreeEpisode: {
    rentalAndPossession: 'contenthome_ticket_option_popup',
    rental: 'contenthome_ticketused_rental_popup',
    possession: 'contenthome_ticketused_possession_popup',
    gift: 'contenthome_ticketused_gift_popup',
    waitForFree: 'contenthome_ticketused_gidamu_popup',
    welcomeGift: 'contenthome_ticketused_gift_popup',
  },
  alreadyRented: {
    rentalAndPossession: 'contenthome_rented_ep_ticket_popup',
    rental: 'contenthome_rented_ep_ticket_rental_popup',
    possession: 'contenthome_rented_ep_ticket_possession_popup',
    gift: 'contenthome_rented_ep_ticket_gift_popup',
    waitForFree: 'contenthome_rented_ep_ticket_gidamu_popup',
    welcomeGift: 'contenthome_rented_ep_ticket_gift_popup',
    empty: 'contenthome_rented_ep_purchase_ticket_popup',
  },
  alreadyRentedExcludedEpisode: {
    rentalAndPossession: 'contenthome_rented_ep_ticket_popup',
    rental: 'contenthome_rented_ep_ticket_rental_popup',
    possession: 'contenthome_rented_ep_ticket_possession_popup',
    gift: 'contenthome_rented_ep_ticket_gift_popup',
    waitForFree: 'contenthome_rented_ep_ticket_gidamu_popup',
    welcomeGift: 'contenthome_rented_ep_ticket_gift_popup',
    empty: 'contenthome_rented_ep_purchase_ticket_popup',
  },
};

export default function* saga() {
  yield all([
    takeLatest(
      ActionType.FetchUseTicket,
      makeFetchSaga({fetchSaga: fetchUseTicket, canCache: false})
    ),
    takeLatest(
      ActionType.FetchTimeout,
      makeFetchSaga({fetchSaga: fetchTimeout, canCache: false})
    ),
  ]);
}
